// import external libraries
import { useEffect } from 'react';

// import constants
import { MRP_SESSION_STORAGE_KEYS } from '../mrp.consts';

// import stores
import { useCalendarStore } from '../stores/calendarStore/calendarStore';

// import types
import type { CalendarSourceData } from '../stores/calendarStore/calendarStore.types';

/**
 * Opens the agenda another page asked for — the takenlijst sends a planner straight to a care receiver's
 * or employee's calendar — and clears the request so a later visit does not reopen it.
 *
 * Waits for the persisted calendar store to rehydrate: opening before that would have the restored
 * calendars land on top of the one just opened. Pinned, like every other calendar opened by an explicit
 * action, so it survives the next entity change.
 */
export const useOpenHandedOverCalendar = () => {
  const hasHydrated = useCalendarStore((state) => state.hasHydrated);
  const openCalendar = useCalendarStore((state) => state.control.openCalendar);

  useEffect(() => {
    if (!hasHydrated) return;

    const stored = window.sessionStorage.getItem(
      MRP_SESSION_STORAGE_KEYS.CALENDAR_TO_OPEN
    );

    if (!stored) return;

    window.sessionStorage.removeItem(MRP_SESSION_STORAGE_KEYS.CALENDAR_TO_OPEN);

    try {
      const source = JSON.parse(stored) as CalendarSourceData;

      if (!source?.planningEntityIdentifier?.entityId) return;

      openCalendar([source], true);
    } catch {
      // A malformed handover is not worth failing the page over — the planner simply lands on the
      // calendars they already had.
    }
  }, [hasHydrated, openCalendar]);
};
